
import React from 'react';
import { Button } from '@/components/ui/button';
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { ItineraryEvent } from '@/types/itinerary';
import EventList from '@/components/itinerary/EventList';
import { reorderEvents } from '../ItineraryUtils';
import { useAuth } from "@/contexts/auth";

interface TimelineTabProps {
  date: string;
  events: ItineraryEvent[];
  canEdit: boolean;
  onEdit?: (event: ItineraryEvent) => void;
  onDelete?: (eventId: string) => void;
  onAddEvent: () => void;
  setEvents: (events: ItineraryEvent[]) => void;
  allEvents: ItineraryEvent[];
}

const TimelineTab: React.FC<TimelineTabProps> = ({
  date,
  events,
  canEdit,
  onEdit,
  onDelete,
  onAddEvent,
  setEvents,
  allEvents
}) => {
  const { user } = useAuth();
  
  // Filter events to only show those created by the current user
  const userEvents = events.filter(event => 
    // If user is logged in, only show their events
    user ? event.created_by === user.id : true
  );

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    
    const { source, destination } = result;
    if (source.droppableId !== destination.droppableId || 
        source.index === destination.index) return;
    
    const updatedEvents = await reorderEvents(
      allEvents, 
      source.index, 
      destination.index, 
      source.droppableId
    );
    
    if (updatedEvents) {
      setEvents(updatedEvents);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <EventList 
          date={date}
          events={userEvents}
          onEdit={canEdit ? onEdit : undefined}
          onDelete={canEdit ? onDelete : undefined}
        />
      </DragDropContext>
      
      {userEvents.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No events scheduled for this day</p>
          {canEdit && (
            <Button 
              variant="outline" 
              onClick={onAddEvent} 
              className="mt-4"
            >
              Add an event
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default TimelineTab;
